/**
 * COMMANDES & RÉCEPTIONS — la page (maquette `isAchats`, portée PIXEL PERFECT, décision utilisateur
 * 2026-08-28) : deux onglets à badge, une barre de filtres au standard du kit (capsule `field-search`,
 * Pickers fournisseur / statut / période), deux tables TRIABLES aux colonnes du dessin (progression de
 * livraison, reste à livrer), le repli des vieux BL intégrés, les actions par ligne (étiquettes, A4,
 * suppression) et les pieds pédagogiques mot pour mot.
 *
 * ⚠️ LE BANDEAU MOBILE PARTNER EST SERVI (`receptions-controles.tsx`) : un contrôle scanné en réserve
 *    arrive en tuile au-dessus de la liste des BL, et le badge de l'onglet le compte avec les bons
 *    ouverts (maquette `acTabs`). Il ne vient QUE de la base — aucune tuile n'est simulée.
 * ⚠️ L'ONGLET BL EST PREMIER ET ACTIF (décision utilisateur 2026-08-29, divergence assumée avec le
 *    dessin — demande n°9 au LISEZ-MOI de la maquette) : le geste quotidien est la réception, et
 *    atterrir sur un SECOND onglet actif était une bizarrerie UX. L'ordre suit l'usage.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { confirm, FilterToolbar, Icon, Picker, showToast, Trans } from '@orgazone/ui-web';
import { api, type BonResume, type CommandeResume, type ReceptionControl } from '../api';
import { fieldLabel } from '../vocabulary';
import { ControlesMobilePartner } from './receptions-controles';
import { CADENCE_RAFRAICHISSEMENT_MS, derniereVerification } from './rafraichissement';
import {
  BtnIcone, dateCourte, ETATS, IconeEtiquette, IconeImprimante, imprimerBonA4,
  libelleBon, lignesAEtiqueterDuBon, MONO, orderStatusTone, StatusPill, Source,
} from './receptions-shared';
import { BonModal } from './receptions-bon';
import { CommandeModal } from './receptions-commande';
import { OrderEditor } from './order-editor';
import { EtiquettesModal } from './receptions-etiquettes';
import type { LigneAEtiqueter } from './receptions-shared';
import { key, t, type Translatable } from '../i18n';

/** Les périodes du picker — libellés et bornes RELEVÉS (maquette `@achPer` / `achPerOk`). */
const PERIODES: { v: string; l: Translatable; days: number }[] = [
  { v: '30j', l: key('30 derniers jours'), days: 30 },
  { v: '3m', l: key('3 derniers mois'), days: 92 },
  { v: '6m', l: key('6 derniers mois'), days: 183 },
  { v: '12m', l: key('12 derniers mois'), days: 365 },
];

/** Les statuts filtrables d'un BL — la liste maquette + « Clos » (état réel) et « Avec écarts ». */
const STATUTS_BL: { v: string; l: Translatable }[] = [
  { v: 'to_check', l: key('À contrôler') }, { v: 'ready', l: key('Contrôlé — à intégrer') },
  { v: 'partial', l: key('Partiel') }, { v: 'received', l: key('Intégré') }, { v: 'closed', l: key('Clos — reliquat') },
  { v: 'ecarts', l: key('Avec écarts') },
];
const STATUTS_CMD: { v: string; l: Translatable }[] = [
  { v: 'waiting', l: key('En attente') }, { v: 'partial', l: key('Partiellement livrée') },
  { v: 'delivered', l: key('Soldée') }, { v: 'closed', l: key('Clôturée') },
];

/** La recherche multi-mots de la maquette : chaque mot doit se retrouver quelque part dans la meule. */
const cherche = (hay: string, q: string): boolean => {
  const meule = hay.toLowerCase();
  return q.trim().toLowerCase().split(/\s+/).every((w) => meule.includes(w));
};

type Tri = { k: string; d: 1 | -1 };

/** L'en-tête de colonne triable — flèche et graisse sur la colonne active (maquette `thead`). */
function TeteTriable({ k, l, droite, tri, onTri }: { k: string; l: string; droite?: boolean; tri: Tri; onTri: (k: string) => void }) {
  const on = tri.k === k;
  return (
    <button type="button" title={t('Trier')} onClick={() => onTri(k)}
      style={{
        border: 'none', background: 'transparent', padding: 0, margin: 0, font: 'inherit', letterSpacing: 'inherit',
        textTransform: 'inherit', color: on ? 'var(--ink-800)' : 'inherit', cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', gap: 4, justifyContent: droite ? 'flex-end' : 'flex-start', whiteSpace: 'nowrap', minWidth: 0,
      }}>
      {l}<span style={{ fontSize: '9px' }}>{on ? (tri.d === 1 ? '▲' : '▼') : ''}</span>
    </button>
  );
}

export function Receptions() {
  const [tab, setTab] = useState<'cmd' | 'bl'>('bl');
  const [bons, setBons] = useState<BonResume[] | null>(null);
  const [commandes, setCommandes] = useState<CommandeResume[] | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  // La barre de filtres — PARTAGÉE entre les onglets (maquette) : le statut seul change avec l'onglet.
  const [q, setQ] = useState('');
  const [sup, setSup] = useState('');
  const [stBl, setStBl] = useState('');
  const [stCmd, setStCmd] = useState('');
  const [period, setPeriode] = useState('');
  const [anciens, setAnciens] = useState(false);

  const [triBl, setTriBl] = useState<Tri>({ k: '', d: 1 });
  const [triCmd, setTriCmd] = useState<Tri>({ k: '', d: 1 });

  const [ouvertBon, setOuvertBon] = useState<string | null>(null);
  const [ouverteCmd, setOuverteCmd] = useState<string | null>(null);
  // ⚠️ UN BROUILLON NE S'OUVRE PAS EN DÉTAIL : il rouvre l'éditeur pour être repris (le dessin).
  const [draftToOpen, setDraftToOpen] = useState<string | null>(null);
  const [creation, setCreation] = useState(false);
  const [etiquettes, setEtiquettes] = useState<{ id: string; lines: LigneAEtiqueter[]; src?: 'done' | 'ctl' | 'wait' } | null>(null);

  /** Les contrôles Mobile Partner en attente — les tuiles du bandeau, lues avec les bons. */
  const [controls, setControls] = useState<ReceptionControl[]>([]);
  const reload = useCallback(async () => {
    try {
      setFailure(null);
      const [b, c, k] = await Promise.all([api.deliveries(), api.orders(), api.deliveryControls()]);
      setBons(b.rows); setCommandes(c.rows); setControls(k.rows);
    } catch (e) {
      setFailure(e instanceof Error ? e.message : String(e));
    }
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  /**
   * LE BANDEAU MOBILE PARTNER SE RAFRAÎCHIT SEUL — sinon il faut recharger la page pour voir arriver
   * un contrôle, et personne ne recharge une page qui a l'air à jour.
   *
   * ⚠️ ON NE RELIT QUE LES CONTRÔLES, pas la page entière : relire bons et commandes toutes les quinze
   * secondes rechargerait la table sous les yeux de qui la lit, pour un bandeau qui tient en trois
   * tuiles. Quand un contrôle est TRAITÉ, là oui, tout est relu — c'est `onChanged`.
   * ⚠️ ON NE BAT PAS DANS UN ONGLET CACHÉ, et on rattrape au retour : c'est le moment où quelqu'un
   * regarde. Même règle que l'écran d'inventaire, et c'est désormais la MÊME cadence, au même endroit.
   * ⚠️ UN BATTEMENT RATÉ EST AVALÉ — il ne doit pas casser un écran qui fonctionne — MAIS le repère
   * « vérifié il y a … » cesse alors d'avancer : l'écran le DIT sans rien interrompre (§23.0).
   */
  const [vuA, setVuA] = useState<number | null>(null);
  const [horloge, setHorloge] = useState(() => Date.now());
  const relireControles = useCallback(async () => {
    try {
      const k = await api.deliveryControls();
      setControls(k.rows);
      setVuA(Date.now());
    } catch { /* voir l'en-tête : best-effort assumé, la fraîcheur cesse d'avancer et le dit. */ }
  }, []);

  useEffect(() => {
    const tic = setInterval(() => { if (!document.hidden) void relireControles(); }, CADENCE_RAFRAICHISSEMENT_MS);
    const auRetour = () => { if (!document.hidden) void relireControles(); };
    document.addEventListener('visibilitychange', auRetour);
    return () => { clearInterval(tic); document.removeEventListener('visibilitychange', auRetour); };
  }, [relireControles]);

  // ⚠️ UNE HORLOGE À PART, plus lente que le battement : c'est elle qui fait VIEILLIR le repère à
  //    l'écran. Sans elle, « vérifié il y a 15 s » resterait figé jusqu'au prochain battement — donc
  //    afficherait la même chose qu'un rafraîchissement en panne.
  useEffect(() => {
    const tic = setInterval(() => setHorloge(Date.now()), 5_000);
    return () => clearInterval(tic);
  }, []);

  const filtreActif = q.trim() !== '' || sup !== '' || period !== '' || (tab === 'bl' ? stBl !== '' : stCmd !== '');

  const dansLaPeriode = useCallback((ms: number | null) => {
    if (!period) return true;
    const p = PERIODES.find((x) => x.v === period);
    if (!p || ms == null) return true;
    return Date.now() - ms <= p.days * 86_400_000;
  }, [period]);

  // ⚠️ LE REPLI DE LA MAQUETTE (`achOldBL`) : un BL intégré ou clos depuis plus d'un mois se replie —
  //    SAUF quand un filtre est actif (chercher doit chercher partout) ou qu'on a déplié.
  const estAncien = useCallback((b: BonResume) => (b.resume.etat === 'received' || b.resume.etat === 'closed')
    && Date.now() - (b.deliveredOn ?? b.createdAt) > 30 * 86_400_000, []);

  const blFiltres = useMemo(() => {
    if (!bons) return [];
    return bons.filter((b) => {
      if (!filtreActif && !anciens && estAncien(b)) return false;
      if (sup && (b.supplierLabel ?? '—') !== sup) return false;
      if (!dansLaPeriode(b.deliveredOn ?? b.createdAt)) return false;
      if (stBl === 'ecarts') { if (!b.resume.hasVariance) return false; }
      else if (stBl && b.resume.etat !== stBl) return false;
      if (q.trim() && !cherche(`${b.number} ${b.supplierLabel ?? ''} ${b.orderRef ?? ''} ${b.sourceRef ?? ''}`, q)) return false;
      return true;
    });
  }, [bons, filtreActif, anciens, estAncien, sup, dansLaPeriode, stBl, q]);

  const cmdFiltrees = useMemo(() => {
    if (!commandes) return [];
    return commandes.filter((c) => {
      if (sup && (c.supplierLabel ?? '—') !== sup) return false;
      if (!dansLaPeriode(c.orderedOn ?? c.createdAt)) return false;
      if (stCmd && c.resume.etat !== stCmd) return false;
      if (q.trim() && !cherche(`${c.number} ${c.supplierLabel ?? ''} ${c.note ?? ''}`, q)) return false;
      return true;
    });
  }, [commandes, sup, dansLaPeriode, stCmd, q]);

  // Le tri — mêmes clés que la maquette ; les colonnes numériques démarrent en DESCENDANT.
  const blTries = useMemo(() => {
    const rows = [...blFiltres];
    const { k, d } = triBl;
    if (!k) return rows;
    const cle = (b: BonResume): string | number => k === 'id' ? b.number : k === 'sup' ? (b.supplierLabel ?? '')
      : k === 'src' ? b.source : k === 'cmd' ? (b.orderRef ?? '') : k === 'prog' ? b.resume.received : libelleBon(b.resume, b.corrected);
    rows.sort((a, b) => { const x = cle(a); const y = cle(b); return (typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y))) * d; });
    return rows;
  }, [blFiltres, triBl]);

  const cmdTriees = useMemo(() => {
    const rows = [...cmdFiltrees];
    const { k, d } = triCmd;
    if (!k) return rows;
    const cle = (c: CommandeResume): string | number => k === 'id' ? c.number : k === 'sup' ? (c.supplierLabel ?? '')
      : k === 'nl' ? c.lineCount : k === 'pc' ? (c.resume.ordered ? c.resume.received / c.resume.ordered : 0)
        : k === 'ral' ? (c.resume.etat === 'closed' ? -1 : c.resume.remaining) : t(orderStatusTone(c).l);
    rows.sort((a, b) => { const x = cle(a); const y = cle(b); return (typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y))) * d; });
    return rows;
  }, [cmdFiltrees, triCmd]);

  const trier = (tri: Tri, set: (t: Tri) => void, numeriques: string[]) => (k: string) => {
    if (tri.k === k) set({ k, d: tri.d === 1 ? -1 : 1 });
    else set({ k, d: numeriques.includes(k) ? -1 : 1 });
  };

  // Le badge de l'onglet BL — les bons encore OUVERTS (ni intégrés, ni clos), comme la maquette.
  const ouverts = (bons ?? []).filter((b) => b.resume.etat !== 'received' && b.resume.etat !== 'closed').length;
  const anciensN = (bons ?? []).filter(estAncien).length;

  const suppliers = useMemo(() => {
    const set = new Set<string>();
    for (const b of bons ?? []) if (b.supplierLabel) set.add(b.supplierLabel);
    for (const c of commandes ?? []) if (c.supplierLabel) set.add(c.supplierLabel);
    return [...set].sort((a, b) => a.localeCompare(b)).map((v) => ({ value: v, label: v }));
  }, [bons, commandes]);

  const effacer = () => { setQ(''); setSup(''); setStBl(''); setStCmd(''); setPeriode(''); };

  const deleteDeliveryNote = useCallback(async (r: BonResume) => {
    const ok = await confirm({
      title: t('Supprimer le BL {number} ?', { number: r.number }),
      message: t('Aucune pièce entrée en stock — le bon disparaît de la liste. S’il venait d’un EDI ou d’un import, il pourra être réimporté.'),
      confirmLabel: t('Supprimer le BL'),
    });
    if (!ok) return;
    try {
      await api.deleteDelivery(r.number);
      showToast(t('{number} supprimé — aucune entrée en stock à défaire', { number: r.number }));
      await reload();
    } catch (e) { showToast(e instanceof Error ? e.message : String(e), 'err'); }
  }, [reload]);

  const supprimerCmd = useCallback(async (r: CommandeResume) => {
    const ok = await confirm({
      title: t('Supprimer la commande {number} ?', { number: r.number }),
      message: t('Aucune pièce réceptionnée — la commande disparaît de la liste. Un EDI ou un import pourra la recréer.'),
      confirmLabel: t('Supprimer la commande'),
    });
    if (!ok) return;
    try {
      await api.deleteOrder(r.number);
      showToast(t('{number} supprimée — aucune réception à défaire', { number: r.number }));
      await reload();
    } catch (e) { showToast(e instanceof Error ? e.message : String(e), 'err'); }
  }, [reload]);

  /** Les actions de ligne qui exigent le DÉTAIL (lignes) — relu à l'instant, jamais deviné. */
  const imprimerBon = useCallback(async (numero: string) => {
    try { const b = await api.delivery(numero); imprimerBonA4(b, libelleBon(b.resume, b.corrected)); }
    catch (e) { showToast(e instanceof Error ? e.message : String(e), 'err'); }
  }, []);
  const etiquettesBon = useCallback(async (numero: string) => {
    try {
      const b = await api.delivery(numero);
      const lines = lignesAEtiqueterDuBon(b);
      const ctl = lines.reduce((n, x) => n + x.ctl, 0); const done = lines.reduce((n, x) => n + x.done, 0);
      setEtiquettes({ id: b.number, lines, src: ctl > 0 ? 'ctl' : done > 0 ? 'done' : 'wait' });
    } catch (e) { showToast(e instanceof Error ? e.message : String(e), 'err'); }
  }, []);
  const etiquettesCmd = useCallback(async (numero: string) => {
    try {
      const c = await api.order(numero);
      const lines: LigneAEtiqueter[] = c.lines.filter((l) => l.known)
        .map((l) => ({ gencode: l.gencode, done: 0, ctl: 0, wait: Math.max(0, l.orderedQty - l.receivedQty) }))
        .filter((l) => l.wait > 0);
      setEtiquettes({ id: c.number, lines, src: 'wait' });
    } catch (e) { showToast(e instanceof Error ? e.message : String(e), 'err'); }
  }, []);

  const enTeteColonnes = {
    display: 'grid', gap: 10, padding: '11px 18px', background: 'var(--ink-50)', borderBottom: '1px solid var(--ink-200)',
    fontSize: '10.5px', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ink-500)',
  } as const;
  const noteDePied = { padding: '13px 18px', background: 'var(--ink-50)', borderTop: '1px solid var(--ink-200)', fontSize: '12.5px', color: 'var(--ink-600)', lineHeight: 1.6 } as const;
  const GRID_BL = '112px minmax(0,1fr) 96px 112px 96px 168px 110px';
  const GRID_CMD = '118px minmax(0,1fr) 76px 172px 96px 158px 110px';

  const videFiltre = (quoi: string) => (
    <div style={{ padding: '34px 18px', textAlign: 'center' }}>
      <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--ink-700)' }}>{quoi}</div>
      <div style={{ fontSize: '12.5px', color: 'var(--ink-500)', marginTop: 4 }}>
        
        {/* ⚠️ UNE PHRASE ENTIÈRE, le bouton porté par un marqueur : « Élargissez la période ou » + bouton + « . »
            laissait l'ordre français à toutes les langues. */}
        <Trans k='Élargissez la période ou <1>effacez les filtres</1>.' parts={[(x) => <button type="button" onClick={effacer} style={{ border: 'none', background: 'transparent', padding: 0, color: 'var(--teal-800)', fontSize: '12.5px', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer' }}>{x}</button>]} />
      </div>
    </div>
  );

  return (
    <>
      {/* ── L'en-tête — titre + sous-titre du dessin, bouton de création selon l'onglet ─────────── */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 250 }}>
          <h1 style={{ margin: 0, fontSize: 'var(--t-h1)', fontWeight: 700, letterSpacing: '-0.025em', color: 'var(--ink-900)' }}>
            
            {t('Commandes & réceptions')}
          </h1>
          <p style={{ margin: '3px 0 0', fontSize: '13.5px', color: 'var(--ink-500)' }}>
            <Trans k='On entre en stock <1>le réel contrôlé</1> — jamais le théorique commandé, jamais un article inconnu.' parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} /></p>
        </div>
        <button
          type="button" onClick={() => setCreation(true)}
          style={{
            height: 40, padding: '0 16px', border: 'none', borderRadius: 'var(--r-sm)', background: 'var(--ink-900)',
            color: 'var(--paper)', fontSize: '13.5px', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap',
          }}
        ><Icon name="plus" size={15} /> {tab === 'cmd' ? t('Nouvelle commande') : t('Nouveau BL')}</button>
      </div>

      {/* ── Les onglets — soulignés teal, badge ambre des BL encore ouverts (maquette acTabs) ───── */}
      <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid var(--ink-200)', marginBottom: 16, overflowX: 'auto' }}>
        {/* ⚠️ LE BADGE COMPTE LES BONS OUVERTS PLUS LES CONTRÔLES EN ATTENTE (maquette acTabs) : un contrôle
            qu'un autre opérateur a scanné doit se voir depuis l'onglet, pas seulement une fois dedans. */}
        {([['bl', t('Réceptions — BL'), ouverts + controls.length], ['cmd', t('Commandes fournisseur'), 0]] as const).map(([k, l, n]) => {
          const on = tab === k;
          return (
            <button aria-pressed={on} key={k} type="button" onClick={() => setTab(k)}
              style={{
                display: 'flex', alignItems: 'center', gap: 7, height: 40, padding: '0 15px', border: 'none', cursor: 'pointer',
                borderBottom: `2px solid ${on ? 'var(--teal-500)' : 'transparent'}`, background: 'transparent',
                color: on ? 'var(--teal-800)' : 'var(--ink-500)', fontSize: '13.5px', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0,
              }}>
              {l}
              {n > 0 && (
                <span style={{ height: 19, minWidth: 19, padding: '0 6px', borderRadius: 'var(--r-pill)', background: 'var(--amber-bg)', color: 'var(--amber-fg)', fontSize: '11px', fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontVariantNumeric: 'tabular-nums' }}>{n}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* ── La barre de filtres — capsule et Pickers DU KIT (règle : le même champ partout) ─────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <FilterToolbar
          title={t('Filtres des réceptions')}
          actifs={(sup ? 1 : 0) + ((tab === 'bl' ? stBl : stCmd) ? 1 : 0) + (period ? 1 : 0)}
          search={
            <div className="field-search">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }} aria-hidden>
                <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.5-4.5" />
              </svg>
              <input
                type="text" value={q} onChange={(e) => setQ(e.target.value)}
                /* ⚠️ Le placeholder promet ce que le filtre TIENT : numéro, fournisseur, commande
                   rattachée, document — la liste n'a pas les lignes, donc pas « article ». */
                placeholder={tab === 'bl' ? t('N°, fournisseur, commande, document…') : t('N°, fournisseur, référence…')}
                aria-label={t('Rechercher dans les commandes et réceptions')}
              />
            </div>
          }
          filters={(
            <>
              <Picker id="ach-sup" label={fieldLabel('supplier')} allLabel={t('Tous les fournisseurs')}
                options={suppliers} value={sup} onChange={setSup} />
              {tab === 'bl' ? (
                <Picker id="ach-st-bl" label={t('Statut')} allLabel={t('Tous les statuts')}
                  options={STATUTS_BL.map((s) => ({ value: s.v, label: t(s.l) }))} value={stBl} onChange={setStBl} />
              ) : (
                <Picker id="ach-st-cmd" label={t('Statut')} allLabel={t('Tous les statuts')}
                  options={STATUTS_CMD.map((s) => ({ value: s.v, label: t(s.l) }))} value={stCmd} onChange={setStCmd} />
              )}
              <Picker id="ach-per" label={t('Période')} allLabel={t('Toute la période')}
                options={PERIODES.map((p) => ({ value: p.v, label: t(p.l) }))} value={period} onChange={setPeriode} />
              {filtreActif && (
                <button type="button" onClick={effacer}
                  style={{ height: 36, padding: '0 11px', border: 'none', borderRadius: 'var(--r-ctrl)', background: 'transparent', color: 'var(--ink-500)', fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
                  <Icon name="x" size={12} />{t('Effacer')}
                </button>
              )}
            </>
          )}
        />
        <span style={{ marginLeft: 'auto', fontSize: '12px', color: 'var(--ink-400)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          {tab === 'cmd' ? t('{n} commande', { n: cmdFiltrees.length }) : t('{n} BL', { n: blFiltres.length })}
        </span>
      </div>

      {/* ── Le bandeau Mobile Partner (maquette osRows) — au-dessus de la table des BL, seulement s'il y a
          quelque chose à décider. Un clic rapproche ou crée le bon, puis l'ouvre : c'est là que l'opérateur
          clique « Entrer les pièces en stock ». */}
      {tab === 'bl' && !failure && bons && commandes && (
        <ControlesMobilePartner
          controls={controls}
          fraicheur={derniereVerification(vuA, horloge)}
          onSettled={(n) => setOuvertBon(n)}
          onChanged={() => void reload()}
        />
      )}
      {failure ? (
        <div role="alert" style={{ padding: '16px 18px', border: '1px solid var(--danger-ink)', borderRadius: 'var(--r-md)', color: 'var(--danger-ink)', fontSize: '13px' }}>
          
          {t('La page n’a pas pu être lue :')} {failure}
          <button type="button" className="btn-ghost" onClick={() => void reload()} style={{ marginLeft: 12 }}>{t('Réessayer')}</button>
        </div>
      ) : !bons || !commandes ? (
        <p style={{ color: 'var(--ink-500)', fontSize: '13px' }}>{t('Lecture…')}</p>
      ) : tab === 'cmd' ? (
        /* ══ ONGLET COMMANDES — N° · Fournisseur · Lignes · Livraison · Reste à livrer · Statut ═══ */
        (
          <div style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <div style={{ minWidth: 860 }}>
                <div style={{ ...enTeteColonnes, gridTemplateColumns: GRID_CMD }}>
                  <TeteTriable k="id" l={t('N° commande')} tri={triCmd} onTri={trier(triCmd, setTriCmd, ['nl', 'pc', 'ral'])} />
                  <TeteTriable k="sup" l={fieldLabel('supplier')} tri={triCmd} onTri={trier(triCmd, setTriCmd, ['nl', 'pc', 'ral'])} />
                  <TeteTriable k="nl" l={t('Lignes')} droite tri={triCmd} onTri={trier(triCmd, setTriCmd, ['nl', 'pc', 'ral'])} />
                  <TeteTriable k="pc" l={t('Livraison')} tri={triCmd} onTri={trier(triCmd, setTriCmd, ['nl', 'pc', 'ral'])} />
                  <TeteTriable k="ral" l={t('Reste à livrer')} droite tri={triCmd} onTri={trier(triCmd, setTriCmd, ['nl', 'pc', 'ral'])} />
                  <TeteTriable k="st" l={t('Statut')} tri={triCmd} onTri={trier(triCmd, setTriCmd, ['nl', 'pc', 'ral'])} />
                  <div />
                </div>
                {cmdTriees.map((r) => {
                  const cls = r.resume.etat === 'closed';
                  const pc = r.resume.ordered ? Math.round(r.resume.received / r.resume.ordered * 100) : 0;
                  const ral = r.resume.remaining;
                  // ⚠️ UN BROUILLON N'A RIEN PROMIS : ni reste à livrer, ni étiquettes de ce qui doit arriver.
                  const isDraft = r.resume.etat === 'draft';
                  const peutSupprimer = r.resume.received === 0;
                  return (
                    <div key={r.number} role="button" tabIndex={0}
                      onClick={() => (r.resume.etat === 'draft' ? setDraftToOpen(r.number) : setOuverteCmd(r.number))}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (r.resume.etat === 'draft') setDraftToOpen(r.number); else setOuverteCmd(r.number); } }}
                      style={{ display: 'grid', gridTemplateColumns: GRID_CMD, gap: 10, padding: '0 18px', minHeight: 50, alignItems: 'center', borderBottom: '1px solid var(--ink-100)', fontSize: '13.5px', cursor: 'pointer' }}>
                      <div style={{ minWidth: 0, fontFamily: MONO, fontSize: '11.5px', fontWeight: 700, color: 'var(--ink-800)' }}>{r.number}</div>
                      {/* ⚠️ PAS de badge de source ici : la maquette n'en dessine pas sur la LISTE des
                          commandes — il vit dans la modale (signalement utilisateur 2026-08-29). */}
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.supplierLabel ?? '—'}</div>
                        <div style={{ fontSize: '11.5px', color: 'var(--ink-500)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {r.note ? `${r.note} · ` : ''}{dateCourte(r.orderedOn ?? r.createdAt)}
                        </div>
                      </div>
                      <div style={{ minWidth: 0, textAlign: 'right', fontSize: '12.5px', color: 'var(--ink-600)', fontVariantNumeric: 'tabular-nums' }}>{r.lineCount}</div>
                      <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ flex: 1, height: 6, borderRadius: 'var(--r-pill)', background: 'var(--ink-100)', overflow: 'hidden', minWidth: 50 }}>
                          <span style={{ display: 'block', height: 6, width: `${pc}%`, borderRadius: 'var(--r-pill)', background: cls ? 'var(--amber)' : 'var(--teal-500)' }} />
                        </span>
                        <span style={{ fontSize: '11.5px', color: 'var(--ink-500)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{r.resume.received} / {r.resume.ordered}</span>
                      </div>
                      <div style={{ minWidth: 0, textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: ral > 0 && !cls && !isDraft ? 'var(--ink-900)' : 'var(--ink-400)' }}>
                        {cls ? t('abandonné') : isDraft ? '—' : ral}
                      </div>
                      <div style={{ minWidth: 0 }}><StatusPill tone={orderStatusTone(r)} /></div>
                      <div style={{ minWidth: 0, display: 'flex', justifyContent: 'flex-end' }}>
                        <span style={{ display: 'inline-flex', gap: 6 }}>
                          <BtnIcone ariaLabel={t('Imprimer les étiquettes du reste à livrer')} disabled={ral <= 0 || isDraft}
                            title={isDraft ? t('Brouillon — rien n’est encore commandé') : ral > 0 ? t('Imprimer les étiquettes — {n} pièce à livrer', { n: ral }) : t('Rien à étiqueter — tout est livré')}
                            onClick={() => void etiquettesCmd(r.number)}><IconeEtiquette /></BtnIcone>
                          <BtnIcone ariaLabel={t('Ouvrir le bon de commande (PDF)')} disabled={isDraft}
                            title={isDraft ? t('Brouillon — pas encore de bon de commande') : t('Ouvrir le bon de commande (PDF)')}
                            onClick={() => window.open(api.orderPdfUrl(r.number), '_blank', 'noopener')}><IconeImprimante /></BtnIcone>
                          <BtnIcone ariaLabel={t('Supprimer la commande')} tone="rose" disabled={!peutSupprimer}
                            title={peutSupprimer ? t('Supprimer la commande — aucune réception') : t('Suppression impossible — des pièces ont été réceptionnées')}
                            onClick={() => void supprimerCmd(r)}><Icon name="trash" size={13} /></BtnIcone>
                        </span>
                      </div>
                    </div>
                  );
                })}
                {/* ⚠️ DEUX VIDES QUI NE DISENT PAS LA MÊME CHOSE : « aucune commande » parle du MAGASIN, « aucune ne correspond »
                    parle des FILTRES. Posés sous la même condition, ils s'affichaient ensemble et l'un des deux mentait toujours. */}
                {commandes.length === 0 && (
                  <div style={{ padding: 30, textAlign: 'center', fontSize: '13px', color: 'var(--ink-500)' }}>
                    
                    {t('Aucune commande fournisseur — elles naissent en amont (EDI, import de fichiers), et peuvent aussi se créer ici à la main pour un réassort téléphonique ou un cas hors flux.')}
                  </div>
                )}
                {commandes.length > 0 && cmdTriees.length === 0 && videFiltre(t('Aucune commande ne correspond à ces filtres'))}
                <div style={noteDePied}>
                  <Trans k='Les commandes naissent <1>en amont</1> — EDI ou import de fichiers — mais une commande peut aussi se créer ici à la main, pour un réassort téléphonique ou un cas hors flux.' parts={[(x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>]} /></div>
              </div>
            </div>
          </div>
        )
      ) : (
        /* ══ ONGLET BL — N° · Fournisseur · Source · Commande · Entré/attendu · Statut ═══════════ */
        (
          <div style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <div style={{ minWidth: 860 }}>
                <div style={{ ...enTeteColonnes, gridTemplateColumns: GRID_BL }}>
                  <TeteTriable k="id" l={t('N° livraison')} tri={triBl} onTri={trier(triBl, setTriBl, ['prog'])} />
                  <TeteTriable k="sup" l={fieldLabel('supplier')} tri={triBl} onTri={trier(triBl, setTriBl, ['prog'])} />
                  <TeteTriable k="src" l={t('Source')} tri={triBl} onTri={trier(triBl, setTriBl, ['prog'])} />
                  <TeteTriable k="cmd" l={t('Commande')} tri={triBl} onTri={trier(triBl, setTriBl, ['prog'])} />
                  <TeteTriable k="prog" l={t('Entré / attendu')} droite tri={triBl} onTri={trier(triBl, setTriBl, ['prog'])} />
                  <TeteTriable k="st" l={t('Statut')} tri={triBl} onTri={trier(triBl, setTriBl, ['prog'])} />
                  <div />
                </div>
                {blTries.map((r) => {
                  const peutSupprimer = r.resume.received === 0;
                  const aQuelqueChose = r.resume.received > 0 || r.resume.checked > 0 || r.resume.expected > 0;
                  return (
                    <div key={r.number} role="button" tabIndex={0}
                      onClick={() => setOuvertBon(r.number)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOuvertBon(r.number); } }}
                      style={{ display: 'grid', gridTemplateColumns: GRID_BL, gap: 10, padding: '0 18px', minHeight: 50, alignItems: 'center', borderBottom: '1px solid var(--ink-100)', fontSize: '13.5px', cursor: 'pointer' }}>
                      <div style={{ minWidth: 0, fontFamily: MONO, fontSize: '11.5px', fontWeight: 700, color: 'var(--ink-800)' }}>{r.number}</div>
                      <div style={{ minWidth: 0 }}>
                        {/* ⚠️ UNE MARQUE QUI ATTEND SON FOURNISSEUR SE VOIT DANS LA LISTE : ce BL n'entrera pas en stock tant
                            qu'elle n'est pas rattachée, et l'apprendre au clic sur « Entrer » serait trop tard. */}
                        <div style={{ fontWeight: 600, color: r.awaitingBrand ? 'var(--amber-fg)' : 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {r.awaitingBrand ? t('{brand} — fournisseur à définir', { brand: r.awaitingBrand }) : r.supplierLabel ?? '—'}</div>
                        <div style={{ fontSize: '11.5px', color: 'var(--ink-500)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {r.sourceRef && !r.sourceRef.startsWith('editor:') ? t('{date} · doc {sourceRef}', { date: dateCourte(r.deliveredOn ?? r.createdAt), sourceRef: r.sourceRef }) : dateCourte(r.deliveredOn ?? r.createdAt)}
                        </div>
                      </div>
                      <div style={{ minWidth: 0 }}><Source s={r.source} /></div>
                      <div style={{ minWidth: 0, fontFamily: MONO, fontSize: '11px', color: r.orderRef ? 'var(--ink-800)' : 'var(--ink-400)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {r.orderRef ?? t('sans commande')}
                      </div>
                      <div style={{ minWidth: 0, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: 'var(--ink-900)' }}>
                        {r.resume.received} / {r.resume.expected}
                      </div>
                      <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <StatusPill tone={ETATS[r.resume.etat]} l={libelleBon(r.resume, r.corrected)} />
                        {r.resume.hasVariance && (
                          <span style={{ height: 20, padding: '0 7px', borderRadius: 'var(--r-xs)', background: 'var(--amber-bg)', color: 'var(--amber-fg)', fontSize: '9.5px', fontWeight: 800, display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap', flexShrink: 0 }}>{t('ÉCARTS')}</span>
                        )}
                      </div>
                      <div style={{ minWidth: 0, display: 'flex', justifyContent: 'flex-end' }}>
                        <span style={{ display: 'inline-flex', gap: 6 }}>
                          <BtnIcone ariaLabel={t('Imprimer des étiquettes')} disabled={!aQuelqueChose}
                            title={t('Imprimer des étiquettes — {entrees}, {controlees}, {attendues}', { entrees: t('{n} entrée', { n: r.resume.received }), controlees: t('{n} contrôlée', { n: Math.max(0, r.resume.checked - r.resume.received) }), attendues: t('{n} attendue', { n: Math.max(0, r.resume.expected - r.resume.received) }) })}
                            onClick={() => void etiquettesBon(r.number)}><IconeEtiquette /></BtnIcone>
                          <BtnIcone ariaLabel={t('Imprimer la liste du BL en A4')} title={t('Imprimer la liste du BL (A4)')}
                            onClick={() => void imprimerBon(r.number)}><IconeImprimante /></BtnIcone>
                          <BtnIcone ariaLabel={t('Supprimer le BL')} tone="rose" disabled={!peutSupprimer}
                            title={peutSupprimer ? t('Supprimer le BL — aucune pièce entrée en stock') : t('Suppression impossible — des pièces sont déjà entrées en stock')}
                            onClick={() => void deleteDeliveryNote(r)}><Icon name="trash" size={13} /></BtnIcone>
                        </span>
                      </div>
                    </div>
                  );
                })}
                {/* ⚠️ MÊME PARTAGE QUE LES COMMANDES, plus un troisième cas : sans filtre, une liste vide de bons tous ANCIENS
                    n'est ni « aucun bon » ni « aucun ne correspond » — le repli dessous le dit, avec leur nombre. */}
                {bons.length === 0 && (
                  <div style={{ padding: 30, textAlign: 'center', fontSize: '13px', color: 'var(--ink-500)' }}>
                    
                    {t('Aucun bon de livraison — ils arrivent par EDI, par import, ou se créent ici')}{' '}
                    <button type="button" onClick={() => setCreation(true)} style={{ border: 'none', background: 'none', padding: 0, color: 'var(--teal-800)', fontSize: '13px', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}>{t('douchette en main')}</button>.
                  </div>
                )}
                {bons.length > 0 && blTries.length === 0 && filtreActif && videFiltre(t('Aucun BL ne correspond à ces filtres'))}
                {/* ⚠️ LE REPLI DES ANCIENS (maquette achOldRow) : sans lui, un magasin repris affiche
                    des années de BL intégrés au-dessus du travail du jour. */}
                {!filtreActif && !anciens && anciensN > 0 && (
                  <button type="button" onClick={() => setAnciens(true)}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', padding: '11px 18px', border: 'none', borderTop: '1px dashed var(--ink-200)', background: 'transparent', color: 'var(--ink-500)', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer' }}>
                    <Icon name="chevronD" size={13} />
                    {t('{n} BL intégré depuis plus d’un mois — afficher', { n: anciensN })}
                  </button>
                )}
                {!filtreActif && anciens && anciensN > 0 && (
                  <button type="button" onClick={() => setAnciens(false)}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', padding: '11px 18px', border: 'none', borderTop: '1px dashed var(--ink-200)', background: 'transparent', color: 'var(--ink-500)', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer' }}>
                    <Icon name="chevronU" size={13} />
                    
                    {t('Masquer à nouveau les BL intégrés anciens')}
                  </button>
                )}
                <div style={noteDePied}>
                  <Trans k='Un BL <1>sans commande est normal</1> — livraison à la volée, scan direct. Et une ligne intégrée <2>ne se modifie plus</2> : si le compte n’y est pas après coup, on fait un BL de correction, on ne réécrit pas l’histoire.' parts={[(x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>, (x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>]} /></div>
              </div>
            </div>
          </div>
        )
      )}

      {/* ⚠️ « NOUVEAU BL » OUVRE L'ÉDITEUR : un BL par fournisseur, au réel reçu, enregistré
          ou intégré tout de suite — la modale « livraison à la volée » a disparu avec son BL né vide. */}
      {creation && tab === 'bl' && (
        <OrderEditor kind="deliveryNote" takenNumbers={(bons ?? []).map((b) => b.number)}
          onClose={() => setCreation(false)}
          onSaved={(_numbers, stillOpen) => { if (!stillOpen) setCreation(false); void reload(); }} />
      )}
      {((creation && tab === 'cmd') || draftToOpen) && (
        <OrderEditor draftNumber={draftToOpen} takenNumbers={(commandes ?? []).map((c) => c.number)}
          onClose={() => { setCreation(false); setDraftToOpen(null); }}
          onSaved={(_numbers, stillOpen) => { if (!stillOpen) { setCreation(false); setDraftToOpen(null); } void reload(); }} />
      )}
      {ouvertBon && <BonModal numero={ouvertBon} onClose={() => { setOuvertBon(null); void reload(); }} />}
      {ouverteCmd && (
        <CommandeModal numero={ouverteCmd}
          onClose={() => { setOuverteCmd(null); void reload(); }}
          onBonCree={(n) => { setOuverteCmd(null); setTab('bl'); setOuvertBon(n); void reload(); }} />
      )}
      {etiquettes && (
        <EtiquettesModal id={etiquettes.id} lines={etiquettes.lines} sourceInitiale={etiquettes.src}
          onClose={() => setEtiquettes(null)} />
      )}
    </>
  );
}
